import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Group } from '@visx/group';
import { BarGroupHorizontal, BarRounded, Line } from '@visx/shape';
import { AxisLeft, AxisTop } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import uniqid from 'uniqid';
import styled from 'styled-components';
import {
  FormatUtils,
  useWindowSize,
  MAP_CHART_FIELDS_LABEL,
  MAP_CHART_FIELDS_THRESHOLD,
  METRICS,
  isReach
} from '../../utils';
import { GridRows } from '@visx/grid';
import StackedChartImage from '../../components/ChartsImages/StackedChartImage';
import ChartTooltip from '../../components/ChartTooltip';
import { useTooltip } from '@visx/tooltip';
import { TooltipData } from '../../components/ChartTooltip/ChartTooltip';
import { localPoint } from '@visx/event';
import { BoxV2, EmptyStateV2 } from '@cubeyou-team/hx-react-components';
import StackedBarValue from '../../components/StackedBarValue';
import StackedTooltipContent from './components/StackedTooltipContent';

const StyledMultipleStackedChartWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #ffffff;
  padding: 10px;
`;

type MultipleStackedChartProps = {
  margin: any;
  data: any[];
  xAxis: string;
  zAxis: string;
  emptyDataMessage?: string;
  limit: any;
  showAxisLeft?: boolean;
  showItemsPreview?: boolean;
  colors: any;
  limitText?: number;
  textWidth?: number;
};
const defaultMargin = { top: 50, right: 50, bottom: 50, left: 100 };
let tooltipTimeout: number;

const MultipleStackedChart: FC<MultipleStackedChartProps> = ({
  margin = defaultMargin,
  data,
  xAxis,
  zAxis,
  emptyDataMessage,
  limit = 'auto',
  showAxisLeft,
  showItemsPreview,
  colors,
  limitText,
  textWidth
}) => {
  const svgRef = useRef(null);
  const chartId = useRef(uniqid());
  const isEmpty = !data || data.length === 0;
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [tooltipType, setTooltipType] = useState('single');
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip
  } = useTooltip<TooltipData>();

  const windowSize = useWindowSize();

  const chartRef = useCallback(
    (node) => {
      if (node != null) {
        setWidth(node.getBoundingClientRect().width);
        setHeight(node.getBoundingClientRect().height);
      }
    },
    [windowSize]
  );

  const keys = (!isEmpty && Object.keys(data[0].metrics)) || [];

  const colorScale = useMemo(
    () =>
      scaleOrdinal<string, string>({
        domain: Object.keys(colors),
        range: Object.values(colors)
      }),
    [colors]
  );

  const filteredData = useMemo(() => {
    if (isEmpty) {
      return [];
    }
    const stackedData = data.map((d) => {
      let bars = [];
      if (d && d.metrics) {
        const sum = [
          ...keys.map((targetId) => d.metrics[targetId][xAxis])
        ].reduce((total, value) => total + value, 0);
        bars = keys.map((target) => {
          return {
            color: colors[target],
            width: sum > 0 ? (d.metrics[target][xAxis] / sum) * 100 : 0
          };
        });
      }

      return {
        ...d,
        bars,
        values: keys.map((key) => {
          return {
            id: d.id,
            color: colors[key],
            ...d.metrics[key]
          };
        })
      };
    });
    return limit !== 'auto' ? stackedData.slice(0, limit) : stackedData;
  }, [isEmpty, data, limit, xAxis, keys]);

  const minMaxValues = useMemo(() => {
    return {
      xMin: Math.min(
        ...filteredData.map((d) =>
          Math.min(...keys.map((key) => d.metrics[key][xAxis]))
        )
      ),
      xMax: Math.max(
        ...filteredData.map((d) =>
          Math.max(...keys.map((key) => d.metrics[key][xAxis]))
        )
      ),
      zMin: isReach(xAxis)
        ? Math.min(
            ...filteredData.map((d) =>
              Math.min(...keys.map((key) => d.metrics[key][zAxis]))
            )
          )
        : Math.min(
            ...filteredData.map((d) =>
              Math.min(...keys.map((key) => d.metrics[key][xAxis]))
            )
          ),
      zMax: isReach(xAxis)
        ? Math.max(
            ...filteredData.map((d) =>
              Math.max(...keys.map((key) => d.metrics[key][zAxis]))
            )
          )
        : Math.max(
            ...filteredData.map((d) =>
              Math.max(...keys.map((key) => d.metrics[key][xAxis]))
            )
          )
    };
  }, [filteredData, keys, xAxis, zAxis]);

  const xzDiff = Math.max(
    minMaxValues.xMax - minMaxValues.xMin,
    minMaxValues.zMax - minMaxValues.zMin,
    0
  );
  const xzMin = Math.min(minMaxValues.xMin, minMaxValues.zMin);
  const xzMax = Math.max(minMaxValues.xMax, minMaxValues.zMax);

  const offsetXZMin =
    MAP_CHART_FIELDS_THRESHOLD[xAxis] > 0 &&
    xzMin >= MAP_CHART_FIELDS_THRESHOLD[xAxis]
      ? MAP_CHART_FIELDS_THRESHOLD[xAxis]
      : (xzMin - xzDiff) / 10;
  const offsetXZMax =
    MAP_CHART_FIELDS_THRESHOLD[xAxis] > 0 &&
    xzMax + xzDiff / 10 < MAP_CHART_FIELDS_THRESHOLD[xAxis]
      ? MAP_CHART_FIELDS_THRESHOLD[xAxis]
      : xzMax + xzDiff / 10;
  const xDomain = useMemo(
    () => [
      METRICS.AFFINITY !== xAxis || offsetXZMin < 0 ? 0 : offsetXZMin,
      offsetXZMax
    ],
    [offsetXZMin, offsetXZMax, xAxis]
  );

  const xScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: xDomain,
        range: [0, xzMax]
      }),
    [xDomain, xzMax]
  );

  const getZValue = useCallback(
    (index) => {
      return Math.max(
        ...keys.map((key) => filteredData[index].metrics[key][zAxis])
      );
    },
    [zAxis, filteredData, keys]
  );

  const y0Scale = useMemo(
    () =>
      scaleBand({
        domain: (filteredData && filteredData.map((item) => item.id)) || [],
        padding: 0.1
      }),
    [filteredData]
  );

  const y1Scale = useMemo(
    () =>
      scaleBand<string>({
        domain: keys,
        padding: 0.25
      }),
    [keys]
  );

  const xTicks = useMemo(() => {
    let min = xzDiff < 1 ? 5 : xzDiff + 4;
    if (xAxis !== METRICS.AFFINITY) {
      min = xzDiff <= 0.01 ? 1 : xzDiff * 100;
    }

    if (width < 520) {
      return Math.min(min, 2);
    }
    return Math.min(min, 10);
  }, [xzDiff, xAxis, width]);

  const getGroupContent = useCallback((group, groups, index) => {
    const options = groups.tickLabelProps(group.value);
    const props = {
      ...options,
      x: group.to.x,
      y: 10 + group.to.y,
      index
    };

    return groups.tickComponent(props);
  }, []);

  const getTickImage = useCallback(
    (d) => {
      const item = filteredData.find((i) => i.id === d);
      return (item && item.pic) || '';
    },
    [filteredData]
  );

  const getTickLabel = useCallback(
    (d) => {
      const item = filteredData.find((i) => i.id === d);
      return (item && item.label) || '';
    },
    [filteredData]
  );

  const barGroupData = useMemo(() => {
    return filteredData.map((d) => {
      const item = { id: d.id };
      keys.forEach((key) => {
        item[key] = d.metrics[key][xAxis];
      });
      return item;
    });
  }, [filteredData, keys, xAxis]);

  const handleMouseLeave = useCallback(
    (event) => {
      tooltipTimeout = window.setTimeout(() => {
        hideTooltip();
        setTooltipType('single');
      }, 0);
    },
    [hideTooltip]
  );

  const handleMouseMove = useCallback(
    (event, index, targetId, color = '#269F49') => {
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      const eventSvgCoords = localPoint(svgRef.current, event);
      setTooltipType('single');
      showTooltip({
        tooltipData: {
          ...filteredData[index].metrics[targetId],
          label: filteredData[index].label,
          color: color
        },
        tooltipTop: eventSvgCoords?.y,
        tooltipLeft: eventSvgCoords?.x
      });
    },
    [showTooltip, filteredData, svgRef]
  );

  const handleImageMouseMove = useCallback(
    (event, index) => {
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      const eventSvgCoords = localPoint(svgRef.current, event);
      setTooltipType('multiple');
      showTooltip({
        tooltipData: {
          ...filteredData[index],
          color: '#c6c6c6'
        },
        tooltipTop: eventSvgCoords?.y,
        tooltipLeft: eventSvgCoords?.x
      });
    },
    [showTooltip, filteredData, svgRef]
  );

  const getTickComponent = useCallback(
    (d) => {
      const index = filteredData.findIndex((i) => i.id === d.id);
      return (
        <React.Fragment
          key={`multiple-stacked-${chartId.current}-category-${d.id}`}
        >
          <StackedChartImage
            d={{ ...d, index }}
            chartId={chartId.current}
            color='#e6e6e6'
            showItemsPreview={showItemsPreview}
            setMinFontSize={filteredData && filteredData.length > 5}
            limitText={limitText}
            textWidth={textWidth}
            onMouseMove={handleImageMouseMove}
            onMouseLeave={handleMouseLeave}
          />
        </React.Fragment>
      );
    },
    [
      filteredData,
      showItemsPreview,
      limitText,
      textWidth,
      chartId.current,
      handleImageMouseMove,
      handleMouseMove
    ]
  );

  const getZBar = useCallback(
    (index) => {
      const barZValue = getZValue(index);
      const barHeight = y0Scale.bandwidth();
      const barZWidth: any =
        barZValue < MAP_CHART_FIELDS_THRESHOLD[zAxis]
          ? xScale(MAP_CHART_FIELDS_THRESHOLD[zAxis]) - xScale(barZValue)
          : xScale(barZValue) - xScale(MAP_CHART_FIELDS_THRESHOLD[zAxis]);
      const barZ =
        barZValue < MAP_CHART_FIELDS_THRESHOLD[zAxis]
          ? xScale(barZValue)
          : xScale(MAP_CHART_FIELDS_THRESHOLD[zAxis]);

      if (barZ >= 0 && barZWidth > 0 && isReach(xAxis)) {
        return (
          <BarRounded
            x={barZ}
            y={0}
            all
            radius={barZWidth / 4}
            width={barZWidth}
            height={barHeight}
            fill='#0ABAA6'
            fillOpacity={0.4}
          />
        );
      }
    },
    [filteredData, getZValue, y0Scale, xScale, xAxis]
  );

  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  y0Scale.rangeRound([0, yMax]);
  y1Scale.rangeRound([0, y0Scale.bandwidth()]);
  xScale.rangeRound([0, xMax]);

  if (isEmpty) {
    return (
      <StyledMultipleStackedChartWrapper>
        <BoxV2 minWidth='100%'>
          <EmptyStateV2
            icon='analytics'
            mainText={emptyDataMessage}
            size='large'
          />
        </BoxV2>
      </StyledMultipleStackedChartWrapper>
    );
  }
  return (
    <StyledMultipleStackedChartWrapper ref={chartRef}>
      <svg ref={svgRef} width={width} height={height}>
        <rect width={width} height={height} fill='#ffffff' rx={14} />
        <GridRows
          top={margin.top}
          left={margin.left}
          scale={y0Scale}
          width={xMax}
          height={yMax}
        />
        <Group top={margin.top} left={margin.left}>
          <BarGroupHorizontal
            data={barGroupData}
            keys={keys}
            width={xMax}
            y0={(item) => item.id}
            y0Scale={y0Scale}
            y1Scale={y1Scale}
            xScale={xScale}
            color={colorScale}
          >
            {(barGroups) =>
              barGroups.map((barGroup) => (
                <Group
                  key={`multiple-stacked-${chartId.current}-bar-group-${barGroup.index}-${barGroup.y0}`}
                  top={barGroup.y0}
                >
                  {getZBar(barGroup.index)}
                  {barGroup.bars.map((bar) => {
                    const barWidth: any =
                      bar.value < MAP_CHART_FIELDS_THRESHOLD[xAxis]
                        ? xScale(MAP_CHART_FIELDS_THRESHOLD[xAxis]) -
                          xScale(bar.value)
                        : xScale(bar.value) -
                          xScale(MAP_CHART_FIELDS_THRESHOLD[xAxis]);
                    const barX =
                      bar.value < MAP_CHART_FIELDS_THRESHOLD[xAxis]
                        ? xScale(bar.value)
                        : xScale(MAP_CHART_FIELDS_THRESHOLD[xAxis]);
                    return (
                      <React.Fragment
                        key={`multiple-stacked-${chartId.current}-bar-${barGroup.index}-${bar.index}-${bar.key}`}
                      >
                        {barX >= 0 && bar.y > 0 && barWidth > 0 && (
                          <BarRounded
                            x={barX}
                            y={bar.y}
                            all
                            width={barWidth}
                            height={bar.height}
                            fill={bar.color}
                            fillOpacity={isReach(xAxis) ? 0.7 : 1}
                            radius={barWidth / 4}
                            onMouseMove={(event) =>
                              handleMouseMove(
                                event,
                                barGroup.index,
                                bar.key,
                                bar.color
                              )
                            }
                            onMouseLeave={(event) => handleMouseLeave(event)}
                          />
                        )}
                        <StackedBarValue
                          threshold={{
                            x: xScale(MAP_CHART_FIELDS_THRESHOLD[xAxis]),
                            value: MAP_CHART_FIELDS_THRESHOLD[xAxis]
                          }}
                          bar={{
                            x: barX,
                            y: bar.y,
                            height: bar.height,
                            width: barWidth,
                            value: bar.value,
                            color: bar.color,
                            formattedValue:
                              !bar.value && xAxis === METRICS.AFFINITY
                                ? '0x'
                                : FormatUtils.formattedValue(bar.value, xAxis)
                          }}
                          onMove={(event) =>
                            handleMouseMove(
                              event,
                              barGroup.index,
                              bar.key,
                              bar.color
                            )
                          }
                          onLeave={(event) => handleMouseLeave(event)}
                        />
                      </React.Fragment>
                    );
                  })}
                </Group>
              ))
            }
          </BarGroupHorizontal>
          <Line
            from={{ x: xScale(MAP_CHART_FIELDS_THRESHOLD[xAxis]), y: 0 }}
            to={{ x: xScale(MAP_CHART_FIELDS_THRESHOLD[xAxis]), y: yMax }}
            stroke='#9493A6'
            strokeWidth='1'
            strokeOpacity={0.5}
          />
          {showAxisLeft && (
            <AxisLeft
              scale={y0Scale}
              tickLabelProps={(d) => ({
                fontSize: '11px',
                pic: getTickImage(d),
                id: d,
                label: getTickLabel(d)
              })}
              tickComponent={(d) => getTickComponent(d)}
            >
              {(axisGroups) => (
                <React.Fragment>
                  {axisGroups.ticks.map((group, index) => {
                    return (
                      <Group
                        key={`multiple-stacked-${chartId.current}-axis-group-${index}`}
                      >
                        {getGroupContent(group, axisGroups, index)}
                      </Group>
                    );
                  })}
                </React.Fragment>
              )}
            </AxisLeft>
          )}
          <AxisTop
            label={MAP_CHART_FIELDS_LABEL[xAxis]}
            top={0}
            scale={xScale}
            numTicks={xTicks}
            stroke='#9493A6'
            tickStroke='#9493A6'
            tickFormat={(d: any) => FormatUtils.formattedValue(d, xAxis)}
            tickLabelProps={() => ({
              fill: '#9493A6',
              fontSize: 11,
              textAnchor: 'middle',
              fontWeight: 'bold',
              dy: -2
            })}
            labelProps={{
              fill: 'black',
              textAnchor: 'middle',
              fontSize: 14,
              fontWeight: 'bold',
              dy: -5
            }}
          />
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <ChartTooltip
          opened={tooltipOpen}
          top={tooltipTop || 0}
          left={tooltipLeft || 0}
          data={tooltipData}
        >
          <StackedTooltipContent
            data={tooltipData}
            chartId={chartId}
            type={tooltipType}
          />
        </ChartTooltip>
      )}
    </StyledMultipleStackedChartWrapper>
  );
};
export default MultipleStackedChart;
